import React from "react";
import { useTranslation } from "react-i18next";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import type { AssignedLimitationInput, LimitationDefinition } from "src/shared/api-types/limitations";

import type { LinkedOperation } from "../types";

import OperationRow from "./OperationRow";

interface OperationsListProps {
  operations: LinkedOperation[];
  userId: number;
  crewId: number;
  aircraftTypeName: string;
  isFiltered: boolean;
  canEdit: boolean;
  limitationDefinitions: LimitationDefinition[];
  onToggle: (
    typeId: number,
    operationId: number,
    enabled: boolean,
    underSupervision: boolean,
    limitations?: AssignedLimitationInput[],
  ) => Promise<unknown>;
  onCopy: (sourceOperation: LinkedOperation, allOperations: LinkedOperation[]) => void;
}

const OperationsList: React.FC<OperationsListProps> = ({
  operations,
  userId,
  crewId,
  aircraftTypeName,
  isFiltered,
  canEdit,
  limitationDefinitions,
  onToggle,
  onCopy,
}) => {
  const { t } = useTranslation();

  if (operations.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("aircraft_management.no_operations")}
        </Typography>
      </Box>
    );
  }

  const handleCopy = (operation: LinkedOperation) => {
    onCopy(operation, operations);
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "35%" }}>{t("aircraft_management.operation")}</TableCell>
            <TableCell style={{ width: "12%", textAlign: "center" }}>{t("aircraft_management.enabled")}</TableCell>
            <TableCell style={{ width: "18%", textAlign: "center" }}>
              {t("aircraft_management.under_supervision")}
            </TableCell>
            <TableCell style={{ width: "25%", textAlign: "center" }}>{t("limitations.title")}</TableCell>
            <TableCell style={{ width: "10%", textAlign: "center" }}>{t("actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {operations.map((op) => (
            <OperationRow
              key={`${op.typeId}:${op.operationId}`}
              operation={op}
              userId={userId}
              crewId={crewId}
              aircraftTypeName={aircraftTypeName}
              canEdit={canEdit}
              limitationDefinitions={limitationDefinitions}
              onToggle={onToggle}
              onCopy={handleCopy}
              showCopy={canEdit && operations.length > 1 && op.operationId === operations[0].operationId}
              otherOperationNames={operations
                .filter((o) => o.operationId !== op.operationId)
                .map((o) => o.operationName)}
              isFiltered={isFiltered}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OperationsList;
